import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  useTrackDeselectedItems,
  useTrackDisabledSelectAll,
  useTrackEnabledIncludeNestedItems,
  useTrackEnabledSelectAll,
} from '@air/analytics';
import { ViewTypeName } from '@air/api/types';
import {
  hydrateSelectedItemsAction,
  resetSelectedItemsAction,
  selectableItemsSelector,
  selectedItemsSelector,
} from '@air/redux-selected-items';

import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { useAirStore } from '~/utils/ReduxUtils';

export type UseSelectionBarSelectAllParams = {
  selectAllTotalCount?: number | null;
  selectAllEnabled?: boolean;
};

export type UseSelectionBarSelectAllResult = {
  /** Drops the selection, and select-all with it. */
  onClearAll: () => void;
  toggleSelectAll: () => void;
  enableIncludeNestedItems: () => void;
  /** Select-all applies at all here — a board view that lists items in a grid or a table. */
  canSelectAllInCurrentView: boolean;
  isSelectAllActive: boolean;
  /** There is a larger set to widen the selection to. */
  canOfferSelectAll: boolean;
  /** Select-all is on, and there are boards in view whose contents it hasn't reached. */
  canIncludeNestedItems: boolean;
  hasSelection: boolean;
};

/**
 * The select-all half of the selection bar: widening a selection to the whole view, pulling nested
 * board contents into it, and clearing the lot. Shared verbatim by both bars' views.
 */
export const useSelectionBarSelectAll = ({
  selectAllTotalCount,
  selectAllEnabled,
}: UseSelectionBarSelectAllParams): UseSelectionBarSelectAllResult => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackDeselectedItems } = useTrackDeselectedItems();
  const { trackDisabledSelectAll } = useTrackDisabledSelectAll();
  const { trackEnabledSelectAll } = useTrackEnabledSelectAll();
  const { trackEnabledIncludeNestedItems } = useTrackEnabledIncludeNestedItems();
  const nestedItemsInitialCountRef = useRef<number | null | undefined>(undefined);
  const selectedItems = useSelector(selectedItemsSelector);
  const selectableItems = useSelector(selectableItemsSelector);
  const boardId = useSelector(centralizedBoardIdSelector);
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const setSelectAll = useViewsSelectAllStore((state) => state.setSelectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
  const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);
  const setIncludeNestedItems = useViewsSelectAllStore((state) => state.setIncludeNestedItems);
  const { isSearchActive } = useIsSearchActive();

  const onClearAll = useCallback(() => {
    if (selectAll) {
      trackDisabledSelectAll({ trigger: 'action-bar', totalSelectionCount: selectAllTotalCount ?? 0 });
    } else {
      trackDeselectedItems({
        fileSelectionCount: 0,
        assetSelectionCount: 0,
        boardSelectionCount: 0,
        totalSelectionCount: 0,
        trigger: 'action-bar',
        type: 'none',
      });
    }
    resetSelectAll();
    dispatch(resetSelectedItemsAction());
  }, [dispatch, resetSelectAll, selectAll, selectAllTotalCount, trackDeselectedItems, trackDisabledSelectAll]);

  const canSelectAllInCurrentView = useMemo(() => {
    if (!boardId || !selectAllEnabled) {
      return false;
    }

    return currentViewTypeName === ViewTypeName.gallery || currentViewTypeName === ViewTypeName.table;
  }, [boardId, currentViewTypeName, selectAllEnabled]);

  const hasBoardsInView = useMemo(() => {
    return selectableItems.some((item) => item.type === 'board');
  }, [selectableItems]);

  const toggleSelectAll = useCallback(() => {
    if (selectAll) {
      trackDisabledSelectAll({ trigger: 'action-bar', totalSelectionCount: selectAllTotalCount ?? 0 });
      resetSelectAll();
      dispatch(resetSelectedItemsAction());
      return;
    }

    setSelectAll(boardId);
    // Select only assets and files (not boards)
    const selectableItems = selectableItemsSelector(store.getState());
    const assetsAndFilesOnly = selectableItems.filter((item) => item.type !== 'board');
    dispatch(hydrateSelectedItemsAction({ selectedItems: assetsAndFilesOnly }));
    trackEnabledSelectAll({ trigger: 'action-bar', totalSelectionCount: assetsAndFilesOnly.length });
  }, [
    boardId,
    dispatch,
    resetSelectAll,
    selectAll,
    selectAllTotalCount,
    setSelectAll,
    store,
    trackDisabledSelectAll,
    trackEnabledSelectAll,
  ]);

  const enableIncludeNestedItems = useCallback(() => {
    // Capture count before state change (before cached query can return)
    nestedItemsInitialCountRef.current = selectAllTotalCount;
    setIncludeNestedItems(true);
  }, [selectAllTotalCount, setIncludeNestedItems]);

  // Fire analytics event when includeNestedItems is enabled, waiting for selectAllTotalCount
  // to update from usePrivateSelectAllTotalCount/usePublicSelectAllTotalCount hooks
  useEffect(() => {
    if (!includeNestedItems) {
      // Reset tracking state when nested items is disabled
      nestedItemsInitialCountRef.current = undefined;
      return;
    }

    // initialCount is set in click handler before state changes
    // Fire event if count has changed from that initial value
    const initialCount = nestedItemsInitialCountRef.current;

    if (initialCount !== undefined && selectAllTotalCount && selectAllTotalCount !== initialCount) {
      trackEnabledIncludeNestedItems({ totalSelectionCount: selectAllTotalCount });
      // Set to current to prevent re-firing
      nestedItemsInitialCountRef.current = selectAllTotalCount;
    }
  }, [includeNestedItems, selectAllTotalCount, trackEnabledIncludeNestedItems]);

  return {
    onClearAll,
    toggleSelectAll,
    enableIncludeNestedItems,
    canSelectAllInCurrentView,
    isSelectAllActive: selectAll,
    canOfferSelectAll: !selectAll && !isSearchActive && !!selectAllTotalCount && selectAllTotalCount > 1,
    canIncludeNestedItems: selectAll && !includeNestedItems && !isSearchActive && hasBoardsInView,
    hasSelection: selectedItems.length > 0,
  };
};
